import { FC, useMemo, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Empty, Table } from 'antd';
import { useTranslate } from '@refinedev/core';
import { validationMessage } from '../../../../../utils/validate';
import TablePagination from '../../../../ui/table-pagination';
import { useLocalPagination } from '../../../../../hooks/useLocalPagination';
import tableStyles from '../../tableStyles.module.scss';
import type { TableProps } from 'antd';

interface StructureProps {
    data: any[];
    group: string;
    [key: string]: any;
}

interface Column {
    dossier_code: string;
    dossier_text: string;
    ectd_path: string;
    file_name: string;
    operation: string;
    title: string;
    upload_code: number;
    upload_text: string;
    uploaded_at: string;
    onFilter: () => void;
}

type Filter = {
    value: string | boolean | number;
    record: any;
};

export const Structure: FC<StructureProps> = ({ data, group, ...props }) => {
    const t = useTranslate();
    const [operationFilter, setOperationFilter] = useState<string | null>(null);

    const onChangeRowClassName = ({ upload_code }: Column) => {
        return upload_code !== 200 && !!upload_code ? 'verror' : '';
    };

    const columns: ColumnsType<Column> = [
        {
            title: t(`${group}.operation`),
            dataIndex: 'operation',
            filters: [
                {
                    text: t(`${group}.new`),
                    value: 'new',
                },
                {
                    text: t(`${group}.append`),
                    value: 'append',
                },
                {
                    text: t(`${group}.replace`),
                    value: 'replace',
                },
                {
                    text: t(`${group}.delete`),
                    value: 'delete',
                },
            ],
            // @ts-ignore
            onFilter: (value: Filter['value'], record: Filter['record']) =>
                record.operation.indexOf(t(`${group}.${value}`)) === 0,
            filterMultiple: false,
            filteredValue: operationFilter ? [operationFilter] : null,
        },
        {
            title: t(`${group}.title`),
            dataIndex: 'title',
        },
        {
            title: t(`${group}.file_name`),
            dataIndex: 'file_name',
        },
        {
            title: t(`${group}.ectd_path`),
            dataIndex: 'ectd_path',
        },
        {
            title: t(`${group}.state_of_structure`),
            dataIndex: '',
            render: (_, record: Column) =>
                validationMessage(+record?.dossier_code),
            width: 120,
        },
        {
            title: t(`${group}.uploaded_at`),
            dataIndex: 'uploaded_at',
        },
        {
            title: t(`${group}.file_state`),
            dataIndex: '',
            render: (_, record: Column) =>
                validationMessage(record?.upload_code),
            width: 120,
        },
    ];

    const normalizedData = useMemo(
        () => (Array.isArray(data) ? data : []),
        [data]
    );

    const mappedData = useMemo(
        () =>
            normalizedData.map(({ operation, ...rest }) => ({
                operation: t(`applications.titles.${operation}`),
                ...rest,
            })),
        [normalizedData, t]
    );

    const filteredData = useMemo(() => {
        if (!operationFilter) return mappedData;

        return mappedData.filter(record => {
            const translated = t(`${group}.${operationFilter}`);
            return (
                typeof record.operation === 'string' &&
                record.operation.indexOf(translated) === 0
            );
        });
    }, [group, mappedData, operationFilter, t]);

    const {
        currentPage,
        pageSize,
        pageSizeOptions,
        onChangePage,
        onChangePageSize,
        startIndex,
        endIndex,
    } = useLocalPagination(filteredData.length);

    const paginatedData = useMemo(
        () => filteredData.slice(startIndex, endIndex),
        [filteredData, startIndex, endIndex]
    );

    const hasData = filteredData.length > 0;

    const handleTableChange: TableProps<Column>['onChange'] = (_, filters) => {
        const nextOperation =
            (filters?.operation?.[0] as string | undefined | null) ?? null;

        setOperationFilter(nextOperation);
        onChangePage(1);
    };

    if (!hasData) {
        return <Empty description={t('applications.titles.no_data')} />;
    }

    return (
        <div className={tableStyles.tableWrapper}>
            <Table
                columns={columns}
                dataSource={paginatedData}
                rowClassName={onChangeRowClassName}
                rowKey={row => {
                    let r = (Math.random() + 1).toString(36).substring(7);
                    return row.title + r;
                }}
                pagination={false}
                className={tableStyles.table}
                {...props}
                onChange={handleTableChange}
            />
            <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={filteredData.length}
                pageSizeOptions={pageSizeOptions}
                onChangePage={onChangePage}
                onChangePageSize={onChangePageSize}
                previousLabel={t(
                    'applications.pagination.previous',
                    'Попередня сторінка'
                )}
                nextLabel={t(
                    'applications.pagination.next',
                    'Наступна сторінка'
                )}
                showDocumentsLabel={t(
                    'applications.pagination.show_documents',
                    'Показувати документи'
                )}
                rangeLabel={(start, end, total) =>
                    `${start}-${end} ${t(
                        'applications.pagination.of_total',
                        'з'
                    )} ${total}`
                }
            />
        </div>
    );
};
